import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchFactionAssets,
  fetchFactionProfiles,
  fetchForcePresets,
  fetchPlaces,
  fetchTerrains,
  type FactionProfileView,
  type ForcePresetView,
  type PlaceView,
  type TerrainView,
  type UnitAsset,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import {
  STEP,
  readDraft,
  summariesOf,
  writeDraft,
  type PartialDraft,
  type Step,
  type StepSummaries,
} from './newMissionDraft';
import { FactionLooks } from './FactionLooks';
import { LoadProblemNote } from './LoadProblemNote';
import { problemOf, type LoadProblem } from './loadProblem';
import { FactionPicker } from './FactionPicker';
import { TheatreAsk } from './TheatreAsk';
import { useSettingDefaults } from './useSettingDefaults';
import { factionLabel } from './factionNames';
import { InvitedEditors } from './InvitedEditors';
import { Shell } from './Shell';
import { StartFromStepOne } from './StartFromStepOne';
import { FactionEmblem } from './FactionEmblem';
import { CandidatesMap } from './CandidatesMap';
import { TerrainImage } from './TerrainImage';
import { fetchTerrainKinds } from '../api/catalog';
import { fetchRecipeWorlds, type RecipeFit } from '../api/missions';
import { STEP2_KEY, readStored, type MissionDraftStep2 } from './newMissionDraft';
import { terrainOrigin } from './terrainOrigin';

/** What the rail calls each step, keyed by the step's number and never by its position. */
const RAIL: Record<Step, string> = {
  [STEP.server]: ca.missionUi.rail.server,
  [STEP.map]: ca.missionUi.rail.map,
  [STEP.enemy]: ca.missionUi.rail.enemy,
  [STEP.intel]: ca.missionUi.rail.intel,
  [STEP.type]: ca.missionUi.rail.type,
  [STEP.ours]: ca.missionUi.rail.ours,
  [STEP.start]: ca.missionUi.rail.start,
  [STEP.decide]: ca.missionUi.rail.decide,
};

/**
 * The seven-step rail (UI doc 5.3): Preset · Mapa · Enemic · Intel · Tipus ·
 * Nosaltres · Qui decideix, vertical, with a one-line summary under every done
 * step.
 *
 * "Nosaltres" comes after "Tipus" on purpose. It is where the squad's make-up is
 * edited, and the recipe is what proposes one: put before the type, the table
 * would have had nothing to be filled from.
 */
/** Where each step lives, so a done step on the rail is a way back to it. */
const STEP_ROUTE: Record<Step, string> = {
  [STEP.server]: '/new',
  [STEP.type]: '/new/type',
  [STEP.map]: '/new/map',
  [STEP.enemy]: '/new/enemy',
  [STEP.intel]: '/new/intel',
  [STEP.ours]: '/new/forces',
  [STEP.start]: '/new/start',
  [STEP.decide]: '/new/decide',
};

export function StepRail({ step, summaries = {} }: { step: Step; summaries?: StepSummaries }) {
  return (
    <nav className="steps" aria-label={ca.newMission}>
      {(Object.values(STEP) as Step[]).map((n) => {
        const label = RAIL[n];
        const summary = n < step ? summaries[n] : undefined;
        const body = (
          <>
            <span className="num" aria-hidden="true">
              {n < step ? <i className="bi bi-check-lg" /> : n}
            </span>
            <span>{label}</span>
            {summary && <span className="summary">{summary}</span>}
          </>
        );
        // A done step is a link back to it: every step keeps what it was told
        // in the draft, so going back loses nothing. The steps ahead are not
        // links, since each needs the ones before it.
        return n < step ? (
          <Link
            key={label}
            to={STEP_ROUTE[n]}
            className="step done"
            title={summary ? `${label}: ${summary}` : label}
          >
            {body}
          </Link>
        ) : (
          <span
            key={label}
            className={`step ${n === step ? 'active' : ''}`}
            aria-current={n === step ? 'step' : undefined}
          >
            {body}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * The quick start's frame: the rail and the invited editors on the left, the
 * step on the right. `aside` is what the picker has chosen: beside it, sticky,
 * on a wide screen, and under it on a narrow one (QUICK_START_REVIEW.md §2.2).
 */
export function QuickStart({
  step,
  summaries,
  aside,
  wide,
  children,
}: {
  step: Step;
  summaries?: StepSummaries;
  aside?: ReactNode;
  /** What takes the whole width under the picker and its details: a map, mostly. */
  wide?: ReactNode;
  children: ReactNode;
}) {
  const asideRef = useRef<HTMLDivElement>(null);
  useStickyTop(asideRef);
  const body = (
    <div className={`main ${aside ? 'split' : ''}`}>
      <div className="picks">{children}</div>
      {aside && (
        <div className="aside" ref={asideRef}>
          {aside}
        </div>
      )}
    </div>
  );
  return (
    <div className="quickstart">
      <aside className="side">
        <StepRail step={step} summaries={summaries} />
        <InvitedEditors />
      </aside>
      {wide ? (
        // The wide part sits outside the picker's grid, so the sticky details
        // column stops at the picker's end instead of riding over the map.
        <div className="stage">
          {body}
          <div className="wide">{wide}</div>
        </div>
      ) : (
        body
      )}
    </div>
  );
}

/**
 * Keeps a sticky column in view without a scroll bar of its own. A column that
 * fits the window sticks 16 px from the top; a taller one scrolls with the page
 * until its bottom is in view and sticks there, so everything in it can be read
 * without the inner scroll the details panel used to have (the owner,
 * 2026-09-28: no content boxed in a scroll while the page has room).
 */
function useStickyTop(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const place = () => {
      const room = window.innerHeight - 32;
      const tall = el.offsetHeight;
      el.style.top = tall > room ? `${Math.round(room + 16 - tall)}px` : '16px';
    };
    place();
    window.addEventListener('resize', place);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(el);
    return () => {
      window.removeEventListener('resize', place);
      observer?.disconnect();
    };
  }, [ref]);
}

/** The details panel: a heading and whatever the picker knows about what is chosen. */
export function Details({ children }: { children: ReactNode }) {
  return (
    <section className="card form details" aria-label={ca.missionUi.details.title}>
      <h2>{ca.missionUi.details.title}</h2>
      {children}
    </section>
  );
}

// Step 1, the server the night is played on, is NewMissionServer.tsx.

// --- step 2: the map -------------------------------------------------------------------------

/** The quick start, step 2: which terrain, and which of its towns to build around. */
export function NewMissionMap() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PartialDraft>(() => readDraft());
  const [terrains, setTerrains] = useState<TerrainView[]>([]);
  const [places, setPlaces] = useState<PlaceView[]>([]);
  const [world, setWorld] = useState(draft.worldName ?? '');
  const [place, setPlace] = useState<string>(draft.preferredPlace ?? '');
  const [search, setSearch] = useState('');
  // The grid of terrains is open until one is chosen; then it folds into the
  // chosen terrain's card and the map of its places takes the whole width.
  const [browsing, setBrowsing] = useState(!draft.worldName);
  /** Why the terrains did not arrive, when they did not: never shown as "no terrain". */
  const [terrainsProblem, setTerrainsProblem] = useState<LoadProblem | null>(null);
  const [terrainsAsked, setTerrainsAsked] = useState(0);

  useEffect(() => {
    if (!draft.serverId) return;
    setTerrainsProblem(null);
    fetchTerrains(draft.serverId)
      .then(setTerrains)
      .catch((err) => setTerrainsProblem(problemOf(err)));
  }, [draft.serverId, terrainsAsked]);

  useEffect(() => {
    if (!draft.modSetHash || !world) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    fetchPlaces(draft.modSetHash, world)
      .then((p) => !cancelled && setPlaces(p))
      .catch(() => !cancelled && setPlaces([]));
    return () => {
      cancelled = true;
    };
  }, [draft.modSetHash, world]);

  const needle = search.trim().toLowerCase();
  const shown = needle
    ? terrains.filter((t) => `${t.displayName} ${t.worldName}`.toLowerCase().includes(needle))
    : terrains;
  const chosen = terrains.find((t) => t.worldName === world) ?? null;
  const settlements = useMemo(() => places.filter((p) => p.settlement), [places]);

  // What each terrain carries, against what the type chosen a step earlier
  // needs: a terrain without an airfield is no place for an airfield seizure,
  // and used to be found out only when the type step greyed the type out.
  const step2 = readStored<MissionDraftStep2>(STEP2_KEY);
  const needs = step2?.needs ?? null;
  const recipeId = step2?.recipeId ?? null;
  const runtimeOnly = needs === 'bridge' || needs === 'road';
  const [kinds, setKinds] = useState<Record<string, string[]> | null>(null);
  useEffect(() => {
    if (!draft.modSetHash || !needs || runtimeOnly) return;
    fetchTerrainKinds(draft.modSetHash)
      .then(setKinds)
      .catch(() => setKinds(null));
  }, [draft.modSetHash, needs, runtimeOnly]);
  // The type judged on every map of this server (ENGINE_DESIGN.md §2.18, §2.20
  // step 3): each map it cannot be played on, with the reason and what fixes it.
  // A platform without the answer falls back to the kinds of ground above.
  const [fits, setFits] = useState<Record<string, RecipeFit> | null>(null);
  useEffect(() => {
    if (!draft.modSetHash || !recipeId) return;
    let live = true;
    fetchRecipeWorlds(recipeId, draft.modSetHash, { era: draft.era ?? null })
      .then((f) => live && setFits(f))
      .catch(() => live && setFits(null));
    return () => {
      live = false;
    };
  }, [draft.modSetHash, recipeId, draft.era]);
  const fitOf = (w: string) => fits?.[w.toLowerCase()] ?? null;
  const lacks = (w: string) => {
    const fit = fitOf(w);
    if (fit) return !fit.compatible;
    if (!needs || runtimeOnly || !kinds) return false;
    const have = kinds[w.toLowerCase()];
    return Array.isArray(have) && !have.includes(needs);
  };
  /** Why a map cannot host the type: the platform's own sentence, else the ground it lacks. */
  const lackReason = (w: string): string | null => {
    const first = fitOf(w)?.reasons[0];
    if (first) return first.reason;
    return needs ? ca.missionUi.lacksGround(needs) : null;
  };

  if (!draft.serverId) {
    return <StartFromStepOne />;
  }

  function next() {
    if (!world || lacks(world)) return;
    setDraft(
      writeDraft({
        worldName: world,
        worldDisplayName: chosen?.displayName ?? world,
        preferredPlace: place || null,
      }),
    );
    navigate('/new/enemy');
  }

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <button type="button" onClick={() => navigate('/new/type')}>
            {ca.back}
          </button>
          <button
            type="button"
            className="primary"
            disabled={!world || lacks(world)}
            onClick={next}
          >
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart step={STEP.map} summaries={summariesOf(draft)}>
        <section className="card form terrain-step" aria-label={ca.pickTerrain}>
          <div className="row between">
            <h2>{ca.pickTerrain}</h2>
            {browsing && (
              <label className="search">
                <input
                  type="search"
                  name="terrainSearch"
                  placeholder={ca.missionUi.searchTerrain}
                  aria-label={ca.missionUi.searchTerrain}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            )}
          </div>
          {chosen && !browsing ? (
            <section className="terrain-chosen" aria-label={ca.missionUi.chosenTerrain}>
              <TerrainImage world={chosen.worldName} label={chosen.displayName} />
              <div className="terrain-chosen-facts">
                <p>
                  <strong>{chosen.displayName}</strong> <span className="muted small">{world}</span>
                </p>
                <p className="muted small">
                  <span>
                    {typeof chosen.worldSize === 'number' && chosen.worldSize > 0
                      ? `${(chosen.worldSize / 1000).toFixed(1)} km`
                      : ca.missionUi.details.terrainUnknownSize}
                  </span>
                  {' · '}
                  <span>{terrainOrigin(chosen)}</span>
                </p>
                <button type="button" className="small" onClick={() => setBrowsing(true)}>
                  <i className="bi bi-arrow-left-right" aria-hidden="true" />
                  {ca.missionUi.changeTerrain}
                </button>
              </div>
              <div className="terrain-place">
                <label>
                  {ca.missionUi.details.aroundPlace}
                  <select
                    name="preferredPlace"
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    disabled={settlements.length === 0}
                  >
                    <option value="">{ca.missionUi.details.aroundPlaceAny}</option>
                    {settlements.map((p) => (
                      <option key={`${p.name}-${p.x}-${p.y}`} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                    {place && !settlements.some((p) => p.name === place) && (
                      <option value={place}>{place}</option>
                    )}
                  </select>
                </label>
                <p className="muted small">
                  {settlements.length === 0
                    ? ca.missionUi.details.noPlaces
                    : ca.missionUi.details.aroundPlaceHint}
                </p>
              </div>
            </section>
          ) : (
            <div className="cards grid pick terrains" role="radiogroup" aria-label={ca.pickTerrain}>
              {[...shown]
                .sort((a, b) => Number(lacks(a.worldName)) - Number(lacks(b.worldName)))
                .map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className={`card ${world === t.worldName ? 'selected' : ''} ${lacks(t.worldName) ? 'lacks' : ''}`}
                    role="radio"
                    aria-checked={world === t.worldName}
                    aria-disabled={lacks(t.worldName) || undefined}
                    aria-label={t.displayName ?? t.worldName}
                    title={lacks(t.worldName) ? (lackReason(t.worldName) ?? undefined) : undefined}
                    onClick={() => {
                      if (lacks(t.worldName)) return;
                      if (world !== t.worldName) setPlace('');
                      setWorld(t.worldName);
                      setBrowsing(false);
                    }}
                  >
                    <TerrainImage world={t.worldName} label={t.displayName} />
                    <strong>{t.displayName}</strong>
                    {typeof t.worldSize === 'number' && t.worldSize > 0 && (
                      <span className="muted small">{(t.worldSize / 1000).toFixed(1)} km</span>
                    )}
                    {lacks(t.worldName) && lackReason(t.worldName) && (
                      <span className="notice warn small">{lackReason(t.worldName)}</span>
                    )}
                  </button>
                ))}
            </div>
          )}
          {browsing && terrains.length > 0 && shown.length === 0 && (
            <p className="muted small">{ca.missionUi.noTerrains}</p>
          )}
          {terrainsProblem ? (
            <LoadProblemNote
              problem={terrainsProblem}
              what="terrains"
              onRetry={() => setTerrainsAsked((n) => n + 1)}
            />
          ) : (
            terrains.length === 0 && <p className="muted small">—</p>
          )}
        </section>
        {world && !browsing && recipeId && draft.modSetHash && (
          <section className="card form" aria-label={ca.missionUi.candidates.mapTitle}>
            <h2>{ca.missionUi.candidates.mapTitle}</h2>
            <CandidatesMap
              recipeId={recipeId}
              world={world}
              modSetHash={draft.modSetHash}
              place={place || null}
              pick="objective"
              onPickObjective={(c) => setPlace(c.name)}
              places={places}
              height={2000}
            />
          </section>
        )}
      </QuickStart>
    </Shell>
  );
}

// --- step 3: the enemy -----------------------------------------------------------------------

/** The sides a faction can fight on; civilians are nobody's enemy. */
const FIGHTING_SIDES = ['west', 'east', 'resistance'] as const;

/**
 * A composition the enemy can have built: one with no base furniture in it. The
 * list offered the group's own bases — the LHD, the camp, the briefing room —
 * as "defences" (2026-09-11); a briefing table, an arsenal or a helipad marks a
 * base, and a base is chosen at "Sortida".
 */
function isDefence(p: ForcePresetView): boolean {
  try {
    const body = JSON.parse(p.bodyJson) as { objects?: { role?: string }[] };
    const roles = new Set(['briefing', 'arsenal', 'medical', 'flag', 'park', 'helipad', 'respawn']);
    return !(body.objects ?? []).some((o) => o.role != null && roles.has(o.role));
  } catch {
    return true;
  }
}

/** The quick start, step 3: which enemy, how strong and how good. */
export function NewMissionEnemy() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PartialDraft>(() => readDraft());
  const [factions, setFactions] = useState<FactionProfileView[]>([]);
  /** Composition presets, for what the enemy built before anybody came. */
  const [defenceOptions, setDefenceOptions] = useState<ForcePresetView[]>([]);
  const [defences, setDefences] = useState<string>(draft.enemyDefences ?? '');
  const [enemy, setEnemy] = useState<number | null>(draft.enemyProfileId ?? null);
  /** Why the factions did not arrive, when they did not: never shown as "no faction". */
  const [factionsProblem, setFactionsProblem] = useState<LoadProblem | null>(null);
  const [factionsAsked, setFactionsAsked] = useState(0);
  /** The chosen faction's own men, so a name that says nothing has a picture beside it; null until asked. */
  const [units, setUnits] = useState<UnitAsset[] | null>(null);

  useEffect(() => {
    if (!draft.modSetHash) return;
    setFactionsProblem(null);
    fetchFactionProfiles(draft.modSetHash)
      .then(setFactions)
      .catch((err) => setFactionsProblem(problemOf(err)));
    fetchForcePresets(draft.modSetHash, 'composition')
      .then((list) => setDefenceOptions(list.filter(isDefence)))
      .catch(() => setDefenceOptions([]));
  }, [draft.modSetHash, factionsAsked]);

  // What the type asks of the enemy (release D, D5.5): irregulars only, and why.
  const [enemyRule] = useState(() => readStored<MissionDraftStep2>(STEP2_KEY)?.enemyRule ?? null);
  const refuseEnemy = enemyRule?.irregular
    ? (p: FactionProfileView) => (p.irregular === false ? enemyRule.reason : null)
    : undefined;
  // Under such a type a regular faction chosen before (or proposed) is not the
  // enemy; with exactly one irregular faction on offer, that one is — shown fixed.
  useEffect(() => {
    if (!refuseEnemy || factions.length === 0) return;
    const ownSide = draft.playerFactionClass ? (draft.playerSide ?? null) : null;
    const admitted = factions.filter(
      (p) =>
        (FIGHTING_SIDES as readonly string[]).includes(p.side) &&
        p.side !== ownSide &&
        p.infantry !== false &&
        refuseEnemy(p) === null,
    );
    const current = factions.find((f) => f.id === enemy);
    if (admitted.length === 1 && enemy !== admitted[0].id) {
      setEnemy(admitted[0].id);
    } else if (current && refuseEnemy(current) !== null) {
      setEnemy(null);
    }
    // refuseEnemy is rebuilt every render from a rule read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factions, enemy, draft.playerFactionClass, draft.playerSide]);
  const chosen = factions.find((f) => f.id === enemy) ?? null;
  // Who makes sense against us tonight: the era chosen at step 1 and where this terrain is.
  const { defaults, placeTerrain } = useSettingDefaults(draft.serverId, draft.worldName, draft.era);

  // With nobody chosen, the first enemy that fits the night is — once, so a pick is never undone.
  const proposedOnce = useRef(false);
  useEffect(() => {
    if (proposedOnce.current || enemy !== null || !defaults || factions.length === 0) return;
    const ownSide = draft.playerFactionClass ? (draft.playerSide ?? null) : null;
    for (const proposed of defaults.enemy) {
      const fit = factions.find(
        (p) =>
          p.factionClass.toLowerCase() === proposed.className.toLowerCase() &&
          (FIGHTING_SIDES as readonly string[]).includes(p.side) &&
          p.side !== ownSide &&
          p.infantry !== false,
      );
      if (fit) {
        proposedOnce.current = true;
        setEnemy(fit.id);
        return;
      }
    }
  }, [defaults, factions, enemy, draft.playerFactionClass, draft.playerSide]);

  /**
   * What the enemy looks like: the faction's own men, with the game's picture
   * of each.
   *
   * A mod set carries factions whose names say nothing to somebody who does not
   * already know the mod - "[JMS] Royal Italian Colonial Corps" is a uniform
   * before it is a name - and the flag beside it is often the only clue. The
   * catalogue has had these classes since the first probe run; this is the
   * first screen to ask for them.
   */
  useEffect(() => {
    if (!draft.modSetHash || !chosen) {
      setUnits(null);
      return;
    }
    let current = true;
    setUnits(null);
    fetchFactionAssets(draft.modSetHash, chosen.factionClass)
      .then((a) => {
        if (current) setUnits(a.units);
      })
      .catch(() => {
        if (current) setUnits([]);
      });
    return () => {
      current = false;
    };
  }, [draft.modSetHash, chosen]);

  // The players' own side, once the step that describes them has chosen a
  // faction: two factions of one side are allies in the game, so offering ours
  // as the enemy offered a mission nobody could fight (F12).
  const ownSide = draft.playerFactionClass ? (draft.playerSide ?? null) : null;

  if (!draft.serverId || !draft.worldName) {
    return <StartFromStepOne />;
  }

  function next() {
    setDraft(
      writeDraft({
        enemyProfileId: chosen?.id ?? null,
        enemyFactionClass: chosen?.factionClass ?? null,
        enemyName: chosen ? factionLabel(chosen) : null,
        enemyDefences: defences || null,
      }),
    );
    navigate('/new/intel');
  }

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <button type="button" onClick={() => navigate('/new/map')}>
            {ca.back}
          </button>
          <button type="button" className="primary" onClick={next}>
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart
        step={STEP.enemy}
        summaries={summariesOf(draft)}
        aside={
          <Details>
            {!chosen ? (
              <p className="muted small">{ca.missionUi.details.pickFirst}</p>
            ) : (
              <EnemyDetails
                chosen={chosen}
                units={units}
                modSetHash={draft.modSetHash}
                defenceOptions={defenceOptions}
                defences={defences}
                onDefences={setDefences}
              />
            )}
          </Details>
        }
      >
        <section className="card form" aria-label={ca.pickEnemy}>
          <h2>{ca.pickEnemy}</h2>
          <TheatreAsk defaults={defaults} onPlace={placeTerrain} />
          <FactionPicker
            profiles={factions}
            value={enemy}
            onChange={(p) => setEnemy(p.id)}
            sides={FIGHTING_SIDES}
            exclude={ownSide ? [ownSide] : []}
            first={(defaults?.enemy ?? []).map((f) => f.className)}
            onlyWithInfantry
            hideSidesByDefault={['west']}
            recent={{ serverId: draft.serverId, step: 'enemy' }}
            label={ca.pickEnemy}
            refuse={refuseEnemy}
            onlyAdmitted={ca.missionUi.factionPicker.onlyIrregular}
          />
          {factionsProblem ? (
            <LoadProblemNote
              problem={factionsProblem}
              what="factions"
              onRetry={() => setFactionsAsked((n) => n + 1)}
            />
          ) : (
            factions.length === 0 && <p className="muted small">—</p>
          )}
        </section>
      </QuickStart>
    </Shell>
  );
}

/** What the enemy step's details panel shows for the chosen faction. */
function EnemyDetails({
  chosen,
  units,
  modSetHash,
  defenceOptions,
  defences,
  onDefences,
}: {
  chosen: FactionProfileView;
  units: UnitAsset[] | null;
  modSetHash?: string;
  defenceOptions: ForcePresetView[];
  defences: string;
  onDefences: (id: string) => void;
}) {
  return (
    <>
      <p className="row who">
        <FactionEmblem
          name={factionLabel(chosen)}
          factionClass={chosen.factionClass}
          side={chosen.side}
          picture={chosen.picture}
          size={56}
        />
        <strong>{factionLabel(chosen)}</strong>
      </p>
      <dl className="facts">
        <dt>{ca.missionUi.details.enemySide}</dt>
        <dd>{ca.sideNames[chosen.side] ?? chosen.side}</dd>
        <dt>{ca.missionUi.details.enemyClass}</dt>
        <dd>{chosen.factionClass}</dd>
        {chosen.rides && chosen.rides !== 'unknown' && (
          <>
            <dt>{ca.missionUi.details.enemyRides}</dt>
            <dd>
              {chosen.rides === 'own'
                ? ca.missionUi.details.enemyRidesOwn
                : chosen.rides === 'groups'
                  ? ca.missionUi.details.enemyRidesGroups(chosen.ridesFaction ?? '')
                  : chosen.rides === 'donor'
                    ? ca.missionUi.details.enemyRidesDonor(chosen.ridesFaction ?? '')
                    : ca.missionUi.details.enemyRidesNone}
            </dd>
          </>
        )}
      </dl>
      <FactionLooks
        title={ca.missionUi.details.enemyLooks}
        hint={ca.missionUi.details.enemyLooksHint}
        units={units}
        modSetHash={modSetHash}
      />
      {defenceOptions.length > 0 && (
        <>
          <label>
            {ca.missionUi.details.enemyDefences}
            <select
              name="enemyDefences"
              value={defences}
              onChange={(e) => onDefences(e.target.value)}
            >
              <option value="">{ca.missionUi.details.enemyDefencesNone}</option>
              {defenceOptions.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">{ca.missionUi.details.enemyDefencesHint}</p>
        </>
      )}
    </>
  );
}
